import { type PlanScenario } from '@fintracks/directus-sdk';
import { canonicalJson, canonicalPlanningExclusions, guaranteeExposureBindingMatches, protectionGuaranteesScenarioMatchesReport, resolveExcludedTargetIds, resolveEffectiveGuaranteeExposures, type ClientContribution, type ClientFinancialEntry, type ClientFollowUp, type ClientGoal, type ClientProtectionNeed, type FamilyMember, type FinancingComparison, type FinancingSimulation, type GuaranteeExposure, type PlanningExclusionSelection, type PrivatePensionSimulation } from '@fintracks/contracts';

function sortedRevisionSources(items: ReadonlyArray<{ id: string; version: number }>) {
  return items.map(({ id, version }) => ({ id, version })).sort((left, right) => left.id.localeCompare(right.id));
}

type ReportFingerprintInput = {
  scenario: PlanScenario;
  goals: readonly ClientGoal[];
  financials: readonly ClientFinancialEntry[];
  guaranteeExposures?: readonly GuaranteeExposure[];
  protection: ClientProtectionNeed | null;
  pensions: readonly PrivatePensionSimulation[];
  financings: readonly FinancingSimulation[];
  financingComparisons: readonly FinancingComparison[];
  contributions: readonly ClientContribution[];
  followUp: readonly ClientFollowUp[];
  pipelineVersion: number;
  family?: readonly Pick<FamilyMember, 'id' | 'version'>[];
  exclusions?: readonly PlanningExclusionSelection[];
};

export async function reportSourceRevisionDigest(input: ReportFingerprintInput): Promise<string> {
  if (!protectionGuaranteesScenarioMatchesReport(input.protection, input.scenario.id)) throw new Error('O diagnóstico canônico de proteção pertence a outro cenário');
  const activeGoals = input.goals.filter((goal) => goal.status === 'active' && (goal.scenarioId === null || goal.scenarioId === input.scenario.id));
  const availableGuaranteeExposures = resolveEffectiveGuaranteeExposures(input.guaranteeExposures ?? [], input.scenario.id);
  const effectiveGuaranteeExposures = resolveEffectiveGuaranteeExposures(input.guaranteeExposures ?? [], input.scenario.id, input.exclusions ?? []);
  if (input.protection?.guaranteesMode === 'canonical') {
    const kinds = new Set(availableGuaranteeExposures.map((exposure) => exposure.kind));
    if (availableGuaranteeExposures.length !== 4 || kinds.size !== 4) throw new Error('O fingerprint do relatório está bloqueado por cardinalidade incompleta de garantias canônicas');
    if (availableGuaranteeExposures.some((exposure) => exposure.bindingState !== 'verified' || !exposure.bindingReceipt || !exposure.bindingReceiptSha256 || !exposure.bindingExposureSha256)) throw new Error('O fingerprint do relatório está bloqueado por exposição de garantia sem binding verificável');
    if ((await Promise.all(availableGuaranteeExposures.map(guaranteeExposureBindingMatches))).some((matches) => !matches)) throw new Error('O fingerprint do relatório está bloqueado por receipt de garantia sem vínculo criptográfico');
  }
  const payload = {
    scenario: { id: input.scenario.id, version: input.scenario.version, assumptionsVersion: input.scenario.assumptionsVersion },
    goals: sortedRevisionSources(activeGoals),
    financials: sortedRevisionSources(input.financials.filter((entry) => entry.active && (entry.kind !== 'expense' || !new Set(resolveExcludedTargetIds(input.exclusions ?? [], input.scenario.id, 'expense')).has(entry.id)))),
    guaranteeExposures: sortedRevisionSources(effectiveGuaranteeExposures),
    protection: input.protection ? { id: input.protection.id, version: input.protection.version } : null,
    pensions: sortedRevisionSources(input.pensions),
    financings: sortedRevisionSources(input.financings),
    financingComparisons: sortedRevisionSources(input.financingComparisons),
    contributions: sortedRevisionSources(input.contributions),
    followUp: sortedRevisionSources(input.followUp),
    pipelineVersion: input.pipelineVersion,
    exclusions: canonicalPlanningExclusions(input.exclusions ?? []),
    ...(input.family === undefined ? {} : { family: sortedRevisionSources(input.family) }),
  };
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonicalJson(payload)));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
